
import React, { useState, useEffect, useMemo } from 'react';
import { AppNode, WorkflowNodeData } from '../../types';
import * as LucideIcons from 'lucide-react';
import { Settings2, X, Code, Mail, Database, Bot, GitFork, List, Users, Clock, Zap, Workflow, Layout, AlertCircle } from 'lucide-react';
import { PropertyField, PropertyFieldConfig } from '../molecules/PropertyField';
import { useEditorConfig } from '../../contexts/EditorContext';
import { TabConfig } from '../../utils/propertyPanelRegistry';
import { VariableDefinition } from '../../utils/variableExtraction';

type LucideIconMap = Record<string, LucideIcons.LucideIcon>;
type ConditionOperator = 'equals' | 'notEquals' | 'contains';

const TAB_ICON_MAP: Record<string, LucideIcons.LucideIcon> = {
    properties: Settings2,
    form: List,
    assignments: Users,
    script: Code,
    http: LucideIcons.Globe,
    email: Mail,
    llm: Bot,
    routing: GitFork,
    conditions: GitFork,
    timer: Clock,
    message: Mail,
    signal: Zap,
    database: Database,
    call: Workflow,
    fileimport: LucideIcons.Upload,
    extraction: LucideIcons.Filter,
    errorHandling: LucideIcons.ShieldAlert,
};

const resolveDynamicIcon = (iconName: string): LucideIcons.LucideIcon | undefined => {
    const icons = LucideIcons as unknown as LucideIconMap;
    const capitalized = iconName.charAt(0).toUpperCase() + iconName.slice(1);
    return icons[iconName] || icons[capitalized];
};

const evaluateFieldCondition = (
    operator: ConditionOperator,
    targetValue: unknown,
    expected: unknown,
): boolean => {
    switch (operator) {
        case 'equals': return targetValue === expected;
        case 'notEquals': return targetValue !== expected;
        case 'contains': return Array.isArray(targetValue) && targetValue.includes(expected);
        default: return true;
    }
};

interface NodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedNode: AppNode | null;
  nodes: AppNode[];
  updateNodeData: (id: string, data: Partial<WorkflowNodeData>) => void;
  onRenameNode: (oldId: string, newId: string) => void;
  variableContext?: VariableDefinition[];
  activeTabId: string;
  setActiveTabId: (id: string) => void;
}

const NodeModal: React.FC<NodeModalProps> = ({ 
    isOpen, 
    onClose, 
    selectedNode, 
    nodes,
    updateNodeData, 
    onRenameNode,
    variableContext = [],
    activeTabId,
    setActiveTabId
}) => {
  const { nodeTypes, propertyRegistry, componentRegistry } = useEditorConfig();
  const [localData, setLocalData] = useState<WorkflowNodeData>({ label: '' });
  const [nodeIdInput, setNodeIdInput] = useState('');
  const [nodeIdError, setNodeIdError] = useState<string | null>(null);
  
  const [viewMode, setViewMode] = useState<'form' | 'code'>('form');
  const [jsonCode, setJsonCode] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedNode && isOpen) {
      setLocalData(selectedNode.data);
      setNodeIdInput(selectedNode.id);
      setNodeIdError(null);
      setViewMode('form');
      setJsonError(null);
    }
  }, [selectedNode, isOpen]);

  const config = useMemo(() => selectedNode ? nodeTypes[selectedNode.type || ''] : null, [selectedNode, nodeTypes]);
  const nodeConfig = useMemo(() => selectedNode ? (propertyRegistry[selectedNode.type || ''] || { type: 'default', tabs: [{ id: 'properties', label: 'General', fields: [] }] }) : null, [selectedNode, propertyRegistry]);

  useEffect(() => {
      if (!isOpen || !nodeConfig || nodeConfig.tabs.length === 0) return;
      if (!nodeConfig.tabs.some(tab => tab.id === activeTabId)) {
          setActiveTabId(nodeConfig.tabs[0].id);
      }
  }, [activeTabId, isOpen, nodeConfig, setActiveTabId]);

  if (!isOpen || !selectedNode || !config || !nodeConfig) return null;

  const handleChange = (key: string, value: unknown) => {
    setLocalData((prev) => ({ ...prev, [key]: value }));
  };

  const toggleViewMode = () => {
      if (viewMode === 'form') {
          setJsonCode(JSON.stringify(localData, null, 2));
          setViewMode('code');
          setJsonError(null);
      } else {
          try {
              const parsed = JSON.parse(jsonCode) as WorkflowNodeData;
              setLocalData(parsed);
              setViewMode('form');
              setJsonError(null);
          } catch (e) {
              const message = e instanceof Error ? e.message : String(e);
              setJsonError("Invalid JSON: " + message);
          }
      }
  };

  const handleSave = () => {
      const nextNodeId = nodeIdInput.trim();
      if (!nextNodeId) {
          setNodeIdError('Node ID is required.');
          return;
      }
      if (nextNodeId !== selectedNode.id && nodes.some(node => node.id === nextNodeId)) {
          setNodeIdError('This node ID is already in use.');
          return;
      }

      if (viewMode === 'code') {
          try {
              const parsed = JSON.parse(jsonCode) as WorkflowNodeData;
              updateNodeData(selectedNode.id, parsed);
              if (nextNodeId !== selectedNode.id) onRenameNode(selectedNode.id, nextNodeId);
              onClose();
          } catch {
              setJsonError("Cannot save: Invalid JSON");
          }
      } else {
          updateNodeData(selectedNode.id, localData);
          if (nextNodeId !== selectedNode.id) onRenameNode(selectedNode.id, nextNodeId);
          onClose();
      }
  };

  const renderIcon = (iconName: string) => {
      const MappedIcon = TAB_ICON_MAP[iconName];
      if (MappedIcon) return <MappedIcon size={16} />;
      const DynIcon = resolveDynamicIcon(iconName);
      return DynIcon ? <DynIcon size={16} /> : <Settings2 size={16} />;
  };

  const shouldRenderField = (field: PropertyFieldConfig) => {
      if (!field.condition) return true;
      const { field: targetField, operator, value } = field.condition;
      const targetValue = (localData as Record<string, unknown>)[targetField];
      return evaluateFieldCondition(operator, targetValue, value);
  };

  const renderTabContent = (tab: TabConfig) => {
      if (tab.specialComponent && componentRegistry[tab.specialComponent]) {
          const Component = componentRegistry[tab.specialComponent];
          return <Component 
            data={localData} 
            onChange={handleChange} 
            nodeType={selectedNode.type} 
            context={variableContext} 
          />;
      }
      
      return (
          <div className="space-y-6">
              {tab.fields.map(field => {
                  if (!shouldRenderField(field)) return null;
                  const isWide = field.type === 'textarea' || field.type === 'object' || field.type === 'array' || field.type === 'expression';
                  return (
                    <div key={field.name} className={isWide ? 'w-full' : 'w-full max-w-md'}>
                        <PropertyField
                            config={field}
                            value={(localData as Record<string, unknown>)[field.name]}
                            onChange={handleChange}
                        />
                    </div>
                  );
              })}
          </div>
      );
  };
  
  const ConfigIcon = (config.icon ? (LucideIcons[config.icon as keyof typeof LucideIcons] as LucideIcons.LucideIcon || Settings2) : Settings2);

  const activeTab = nodeConfig.tabs.find(t => t.id === activeTabId) || nodeConfig.tabs[0];
  const resolvedActiveTabId = activeTab.id;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
       <div className="bg-white dark:bg-slate-950 fixed inset-4 md:inset-8 rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200">
          
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shrink-0">
             <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700" style={{ color: config.color }}>
                    <ConfigIcon size={20} />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                        {localData.label || 'Untitled'}
                    </h2>
                    <p className="mt-0.5 text-xs text-slate-400 font-mono">{nodeIdInput}</p>
                </div>
             </div>
             
             <div className="flex items-center gap-2">
                 <button
                    onClick={toggleViewMode}
                    className={`p-2 rounded-lg transition-colors border ${viewMode === 'code' ? 'bg-teal-50 border-teal-200 text-teal-600 dark:bg-teal-900/20 dark:border-teal-800 dark:text-teal-400' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-400'}`}
                    title={viewMode === 'form' ? "Switch to JSON Code" : "Switch to Form View"}
                 >
                     {viewMode === 'form' ? <Code size={20} /> : <Layout size={20} />}
                 </button>
                 <button 
                    onClick={onClose} 
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                 >
                     <X size={20} />
                 </button>
             </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
             {viewMode === 'form' ? (
                 <>
                    <div className="w-48 bg-slate-50 dark:bg-slate-900/50 border-r border-slate-200 dark:border-slate-800 overflow-y-auto p-2 space-y-1 shrink-0">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 px-2 mt-1">
                            Configuration
                        </div>
                        {nodeConfig.tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTabId(tab.id)}
                                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm font-medium transition-all group
                                    ${resolvedActiveTabId === tab.id
                                        ? 'bg-white dark:bg-slate-800 text-teal-700 dark:text-teal-400 shadow-sm ring-1 ring-slate-200 dark:ring-slate-700' 
                                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900'
                                    }
                                `}
                            >
                                <span className={`opacity-70 group-hover:opacity-100 ${resolvedActiveTabId === tab.id ? 'text-teal-600' : ''}`}>
                                    {renderIcon(tab.id)}
                                </span>
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="flex-1 overflow-y-auto bg-white dark:bg-slate-950 relative">
                        <div className="h-full flex flex-col">
                            {nodeConfig.tabs.map(tab => {
                                if (tab.id !== resolvedActiveTabId) return null;
                                return (
                                    <div key={tab.id} className="animate-in fade-in slide-in-from-bottom-2 duration-300 h-full flex flex-col">
                                        {!tab.specialComponent ? (
                                            <div className="p-8">
                                                {tab.id === 'properties' && (
                                                    <div className="mb-6 w-full max-w-md space-y-1.5">
                                                        <label htmlFor="modal-node-id" className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center justify-between">
                                                            <span>Node ID <span className="text-teal-600 ml-1">*</span></span>
                                                        </label>
                                                        <input
                                                            id="modal-node-id"
                                                            type="text"
                                                            value={nodeIdInput}
                                                            onChange={(event) => {
                                                                setNodeIdInput(event.target.value);
                                                                setNodeIdError(null);
                                                            }}
                                                            aria-invalid={Boolean(nodeIdError)}
                                                            aria-describedby={nodeIdError ? 'modal-node-id-error' : undefined}
                                                            className={`w-full px-3 py-2 text-sm font-mono border rounded-lg bg-white dark:bg-slate-900 focus:outline-none focus:ring-1 ${nodeIdError ? 'border-red-500 focus:ring-red-500/20' : 'border-slate-300 dark:border-slate-700 focus:border-teal-500 focus:ring-teal-500/20'}`}
                                                        />
                                                        {nodeIdError && <p id="modal-node-id-error" className="mt-1 text-[11px] text-red-600 dark:text-red-400">{nodeIdError}</p>}
                                                    </div>
                                                )}
                                                {renderTabContent(tab)}
                                            </div>
                                        ) : (
                                            <div className="flex-1 min-h-0">
                                                {renderTabContent(tab)}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                 </>
             ) : (
                 <div className="flex-1 flex flex-col bg-white dark:bg-slate-950 relative">
                     <div className="flex items-center justify-between px-4 py-2 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400">
                         <span className="font-mono">JSON Source</span>
                         {jsonError ? (
                             <span className="text-red-500 dark:text-red-400 flex items-center gap-1 font-bold">
                                 <AlertCircle size={12}/> {jsonError}
                             </span>
                         ) : (
                             <span className="text-emerald-600 dark:text-emerald-500">Valid JSON</span>
                         )}
                     </div>
                     <textarea
                        value={jsonCode}
                        onChange={(e) => {
                            setJsonCode(e.target.value);
                            try {
                                JSON.parse(e.target.value);
                                setJsonError(null);
                            } catch (_err) { /* validation only */ }
                        }}
                        className="flex-1 w-full p-4 bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-300 font-mono text-sm outline-none resize-none"
                        spellCheck={false}
                     />
                 </div>
             )}
          </div>
          
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex justify-end gap-3 shrink-0">
                <button 
                    onClick={onClose} 
                    className="px-5 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-white border border-transparent hover:border-slate-200 rounded-lg transition-all"
                >
                    Cancel
                </button>
                <button 
                    onClick={handleSave} 
                    disabled={viewMode === 'code' && !!jsonError}
                    className="px-5 py-2 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-lg shadow-sm shadow-teal-600/20 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Save Configuration
                </button>
          </div>
       </div>
    </div>
  );
};

export default NodeModal;
